// Percorso file: src/pages/AttendanceSummary.jsx
//
// RIEPILOGO PRESENZE DELL'ALLENATORE — cantiere ATTENDANCE, fetta F2
// (§5.2 del disegno).
//
// Mostra, per UNA squadra e per l'ANNO OPERATIVO, chi c'era e chi no, e
// permette all'allenatore di correggere ogni casella coi cinque stati
// (`yes`, `no`, `pending`, `injured`, `suspended`).
//
// ⚠️ LA SQUADRA ARRIVA DAL PARAMETRO DI ROTTA, non si deduce qui. La sceglie
// l'allenatore nella SUA home (HomeCoach: `squadraScelta`, stato interno che
// senza un parametro non uscirebbe di lì) e viaggia in
// `/attendance/team/:teamName`, esattamente come per `/my-calendar/team/:teamName`.
// La pagina la RICEVE: niente selettore in pagina, niente `location.state`
// (che non sopravvive al ricaricamento e nel frontend non ha uno scrittore
// vivo), niente «prima tessera del coach» — che con due squadre sceglieva a
// caso.
//
// ⚠️ UNA SOLA CHIAMATA PER I DATI: `GET /attendance/team/:teamName` (fetta
// F1) risponde `{ players, attendance }`. Sono spariti:
//   · `GET /attendance` — la rotta NON ESISTE PIÙ: scaricava l'intera
//     collezione, ogni squadra e ogni annata, senza cancello;
//   · `GET /users` — riservata agli amministratori (`soloAdminPerElenco`):
//     un allenatore ne riceveva un 403 e si ritrovava la tabella vuota;
//   · la costruzione dell'elenco dai «quattro campi-squadra»
//     (`teams`/`assignedTeams`/`team`/`contexts[].team`), di cui due morti.
// Il filtro «chi milita in questa squadra, con ruolo canonico PLAYER,
// nell'anno operativo» vive ora sul SERVER, dove non si può aggirare.
//
// ⚠️ LA CHIAVE DI UN GIOCATORE È `u.id`, l'id del suo DOCUMENTO, che è ciò
// che la proiezione restituisce e ciò che `POST /attendance` scrive nel
// campo `playerUid`. Non è l'uid del token: i due coincidono per alcuni e
// non per altri, e usare il secondo farebbe sparire le presenze appena
// salvate.
//
// ⚠️ IL GATE STA A MONTE, ed è un COMPONENTE ESTERNO per lo stesso motivo
// spiegato in Attendance.jsx: in React gli hook non si saltano con un `if`,
// e finché il gate viveva dentro la pagina — dopo gli hook — le chiamate
// partivano comunque. Il componente esportato non ha effetti e non nomina
// `api`. ADMIN e SUPERADMIN senza cappello COACH sono respinti qui: non
// esiste una vista amministrativa delle presenze (decisione del cantiere).
//
// ⚠️ L'ALERT PFA («nome in rosso per chi è marcato nell'annata») NON C'È
// PIÙ, ed è una conseguenza della proiezione minima della rotta nuova: fra i
// cinque campi che il server restituisce — id, firstName, lastName, gender,
// dob — `alertSeasons` non c'è. Il fatto è registrato nella consegna della
// fetta come punto da decidere, non nascosto qui.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaBan,
  FaCheck,
  FaClock,
  FaFileCsv,
  FaNotesMedical,
  FaTimes
} from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { isActingAs } from "../utils/roleUtils";
import { eDiUnaMiaSquadra } from "../utils/squadre";
import { categoriaAnagrafica } from "../utils/categoria";
import { dataEvento, evTimeStamp } from "../utils/calendario";
import "./AttendanceSummary.css";

// Il breakpoint degli strumenti admin (doc 07 §8.1): sopra, la tabella;
// sotto, le card impilate. È scritto a mano di proposito — le media query
// non leggono le custom properties — ed è lo stesso valore che Messaging usa
// per la sua soglia compatta.
const SOGLIA_TABELLA = 768;

// Quanti giorni in avanti guarda il riepilogo. Valore della resa storica,
// invariato: l'allenatore prepara l'appello dei prossimi tre giorni.
const GIORNI_AVANTI = 3;

// I cinque stati, nell'ordine in cui si mostrano. Le etichette sono quelle
// che il socio legge; il valore è quello che il backend accetta
// (`STATI_PRESENZA`, server.js). Le icone sono Font Awesome (§5 «Icone»: le
// emoji come icone sono abolite ovunque — la resa storica usava ✔ ❌ 🩹 ⛔).
const STATI = [
  { valore: "yes", etichetta: "Present", Icon: FaCheck },
  { valore: "no", etichetta: "Absent", Icon: FaTimes },
  { valore: "pending", etichetta: "Pending", Icon: FaClock },
  { valore: "injured", etichetta: "Injured", Icon: FaNotesMedical },
  { valore: "suspended", etichetta: "Suspended", Icon: FaBan }
];

const STATO_DI = Object.fromEntries(STATI.map((s) => [s.valore, s]));

// ===========================================================================
// AIUTANTI DI DATA — `dataEvento` ed `evTimeStamp` dalla sede unica
// utils/calendario.js (giro G-K1)
// ===========================================================================

/** Il tipo dell'evento, col metro del calendario (MyCalendar.jsx). */
const eAllenamento = (ev) => ev && ev.type === "Training";

const uguali = (a, b) => String(a) === String(b);

/** Giorno + data brevi, la stessa forma delle card del calendario. */
function etichettaBreve(ev) {
  const d = dataEvento(ev);
  if (!d) return String(ev?.date || "");

  return `${d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${d.toLocaleDateString(
    "en-AU",
    { day: "2-digit", month: "2-digit" }
  )}`;
}

/**
 * Il file CSV. Corpo della resa storica, con due sole differenze:
 * la colonna «Pending» ora è davvero prodotta (prima si leggeva
 * `stats.pending`, che `getPlayerStats` non restituiva: la colonna usciva
 * vuota per tutti), e le celle portano una LETTERA invece di un'emoji —
 * un foglio di calcolo le allinea e le filtra, un'emoji no.
 */
function esportaCSV(intestazioni, righe, nomeFile) {
  const cella = (v) =>
    typeof v === "string" ? `"${v.replace(/"/g, '""')}"` : v;

  const csv = [
    intestazioni.map(cella).join(","),
    ...righe.map((r) => r.map(cella).join(","))
  ].join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeFile;
  link.click();

  // ⚠️ LA REVOCA È RIMANDATA, non subito dopo il click. Revocare l'URL nella
  // stessa passata sincrona lo invalida prima che il download sia partito:
  // su Firefox il file non arriva affatto. Un `setTimeout(…, 0)` basta —
  // lascia finire il giro corrente, e il download è già avviato.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ===========================================================================
// IL CANCELLO — componente esterno, senza effetti e senza chiamate
// ===========================================================================
export default function AttendanceSummary() {
  const { user, activeContext } = useAuth() || {};
  const { teamName } = useParams();
  const squadra = String(teamName || "").trim();

  // «Indossa ADESSO il cappello COACH», col vocabolario canonico: MANAGER,
  // TD e MANAGERTD valgono COACH (ROLE_EQUIVALENTS, auth/access.js), e con
  // il confronto crudo di prima — `=== "COACH" || === "MANAGER"` — TD e
  // MANAGERTD erano respinti a torto.
  const indossaCoach = isActingAs(user, activeContext, "COACH");

  if (!user || !indossaCoach) {
    return (
      <HomeShell backTo="/home" showSwitch={false}>
        <h1 className="qpsc-page-title">Attendance</h1>
        <p className="qpsc-body-text">Unauthorized access.</p>
      </HomeShell>
    );
  }

  // Indirizzo digitato senza squadra: si dice dove si sceglie, e NON si
  // indovina una squadra al posto suo. Nessuna chiamata parte.
  if (!squadra) {
    return (
      <HomeShell backTo="/home" showSwitch={false}>
        <h1 className="qpsc-page-title">Attendance</h1>
        <p className="qpsc-body-text">
          Open a team from your home page to see its attendance.
        </p>
      </HomeShell>
    );
  }

  return <RiepilogoDellaSquadra squadra={squadra} />;
}

// ===========================================================================
// LA PAGINA — montata solo per chi ha superato il cancello
// ===========================================================================
function RiepilogoDellaSquadra({ squadra }) {
  const navigate = useNavigate();
  const { operatingSeason } = useAuth() || {};
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [events, setEvents] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState("");

  // Quale schermata si vede sotto la soglia. Con un ASCOLTATORE, non
  // leggendo `window.innerWidth` dentro la resa: è il pattern di
  // Messaging.jsx, dove la lettura a mano non reagiva al ridimensionamento.
  const [larghezza, setLarghezza] = useState(() =>
    typeof window === "undefined" ? SOGLIA_TABELLA : window.innerWidth
  );

  useEffect(() => {
    const misura = () => setLarghezza(window.innerWidth);

    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  }, []);

  const compatto = larghezza < SOGLIA_TABELLA;

  // Su telefono si guarda per GIOCATORE o per EVENTO. «Apri l'allenamento di
  // stasera → spunta chi c'è» è un PIVOT LATO CLIENT degli stessi dati già
  // scaricati: nessuna chiamata nuova, nessuna rotta nuova.
  const [vista, setVista] = useState("player");
  const [apertoPlayer, setApertoPlayer] = useState("");
  const [apertoEvento, setApertoEvento] = useState("");

  // La casella che si sta correggendo: { eventId, playerId, nome, stato }.
  const [cella, setCella] = useState(null);
  const [nuovoStato, setNuovoStato] = useState("yes");
  const [salvataggio, setSalvataggio] = useState(false);

  // -----------------------------------------------------------------------
  // I DATI — la rotta nuova, una sola, e gli eventi dal calendario.
  // -----------------------------------------------------------------------
  const caricaRiepilogo = useCallback(async () => {
    try {
      const dati = await api.get(
        `/attendance/team/${encodeURIComponent(squadra)}`
      );

      setPlayers(Array.isArray(dati?.players) ? dati.players : []);
      setAttendance(Array.isArray(dati?.attendance) ? dati.attendance : []);
      setErrore("");
    } catch {
      // Rifiuto e guasto di rete finiscono nello stesso ramo, di proposito:
      // distinguerli direbbe a un estraneo quali squadre esistono.
      setPlayers([]);
      setAttendance([]);
      setErrore("This team's attendance is not available.");
    }
  }, [squadra]);

  const caricaEventi = useCallback(async () => {
    try {
      const dati = await api.get("/events");
      const tutti = Array.isArray(dati) ? dati : [];

      const oggi = new Date();
      oggi.setHours(0, 0, 0, 0);

      const limite = new Date(oggi);
      limite.setDate(limite.getDate() + GIORNI_AVANTI);

      // Il filtro di squadra passa dal metro di casa (`eDiUnaMiaSquadra`,
      // che normalizza i DUE lati) e NON comprende la sentinella `"all"`
      // degli eventi di club: le presenze sono per-squadra, e un evento di
      // club non ne ha una.
      const miei = tutti
        .filter((ev) => eDiUnaMiaSquadra(ev.team, [squadra]))
        .filter((ev) => {
          const d = dataEvento(ev);
          if (!d) return false;
          d.setHours(0, 0, 0, 0);
          return d <= limite;
        })
        .sort((a, b) => evTimeStamp(b) - evTimeStamp(a));

      setEvents(miei);
    } catch {
      setEvents([]);
    }
  }, [squadra]);

  useEffect(() => {
    let vivo = true;

    setCaricamento(true);

    Promise.all([caricaRiepilogo(), caricaEventi()]).finally(() => {
      if (vivo) setCaricamento(false);
    });

    return () => {
      vivo = false;
    };
  }, [caricaRiepilogo, caricaEventi]);

  // -----------------------------------------------------------------------
  // LA MATRICE — un solo attraversamento delle presenze, non uno per
  // giocatore: `getPlayerStats` rifiltrava l'intero elenco a ogni riga e a
  // ogni cella, ed è la stessa risposta calcolata decine di volte.
  //
  // ⚠️ `pending` ORA SI CONTA. La resa storica lo leggeva nell'export
  // (`stats.pending`) ma non lo produceva mai: la colonna «Pending» del CSV
  // usciva vuota per tutti.
  // -----------------------------------------------------------------------
  const statistiche = useMemo(() => {
    const perEvento = new Map();
    for (const ev of events) perEvento.set(String(ev.id), true);

    const perGiocatore = new Map();

    for (const voce of attendance) {
      if (!perEvento.has(String(voce.eventId))) continue;

      const chiave = String(voce.playerUid);
      if (!perGiocatore.has(chiave)) perGiocatore.set(chiave, new Map());
      perGiocatore.get(chiave).set(String(voce.eventId), voce.status);
    }

    const esito = new Map();

    for (const p of players) {
      const stati = perGiocatore.get(String(p.id)) || new Map();

      let present = 0;
      let absent = 0;
      let injured = 0;
      let pending = 0;
      let suspended = 0;

      for (const ev of events) {
        const stato = stati.get(String(ev.id)) || null;
        if (stato === "yes") present += 1;
        else if (stato === "no") absent += 1;
        else if (stato === "injured") injured += 1;
        else if (stato === "pending") pending += 1;
        else if (stato === "suspended") suspended += 1;
      }

      esito.set(String(p.id), {
        stati,
        present,
        absent,
        injured,
        pending,
        suspended,
        percent: events.length ? Math.round((present / events.length) * 100) : 0
      });
    }

    return esito;
  }, [players, events, attendance]);

  const vuote = { stati: new Map(), present: 0, absent: 0, injured: 0, pending: 0, suspended: 0, percent: 0 };
  const statsDi = (playerId) => statistiche.get(String(playerId)) || vuote;
  const statoDi = (playerId, eventId) => statsDi(playerId).stati.get(String(eventId)) || null;

  const mediaSquadra = useMemo(() => {
    if (!players.length || !events.length) return 0;

    let presenti = 0;
    for (const voce of statistiche.values()) presenti += voce.present;

    return Math.round((presenti / (players.length * events.length)) * 100);
  }, [players, events, statistiche]);

  const nomeDi = (p) => `${p.firstName || ""} ${p.lastName || ""}`.trim();

  // -----------------------------------------------------------------------
  // LA CORREZIONE — `POST /attendance`, con `playerUid` = ID DEL DOCUMENTO.
  // Il corpo porta tre campi: `team` e `date` li prende il backend
  // dall'evento (fetta F1) e ignora quelli del corpo.
  // -----------------------------------------------------------------------
  const salvaCella = async () => {
    if (!cella || salvataggio) return;

    setSalvataggio(true);

    try {
      await api.post("/attendance", {
        eventId: cella.eventId,
        playerUid: cella.playerId,
        status: nuovoStato
      });

      setCella(null);
      await caricaRiepilogo();
    } catch {
      setErrore("Could not save. Please try again.");
    } finally {
      setSalvataggio(false);
    }
  };

  const apriCella = (player, ev) => {
    const stato = statoDi(player.id, ev.id);

    setCella({
      eventId: ev.id,
      playerId: player.id,
      nome: nomeDi(player),
      evento: etichettaBreve(ev)
    });
    setNuovoStato(stato || "yes");
  };

  // -----------------------------------------------------------------------
  // L'EXPORT — le stesse colonne di prima, con «Pending» finalmente piena.
  // -----------------------------------------------------------------------
  const esporta = () => {
    const intestazioni = [
      "Player",
      "Present",
      "Absent",
      "Pending",
      "Injured",
      "Suspended",
      "% Present",
      ...events.map(
        (ev) => `${ev.date} ${ev.type || ""}${ev.opponent ? ` vs ${ev.opponent}` : ""}`.trim()
      )
    ];

    const righe = players.map((p) => {
      const s = statsDi(p.id);

      return [
        nomeDi(p),
        s.present,
        s.absent,
        s.pending,
        s.injured,
        s.suspended,
        `${s.percent}%`,
        ...events.map((ev) => {
          const stato = s.stati.get(String(ev.id));
          if (!stato) return "-";
          return stato.charAt(0).toUpperCase();
        })
      ];
    });

    esportaCSV(intestazioni, righe, `attendance-${squadra}.csv`);
  };

  // =========================================================================
  // RESA
  // =========================================================================
  return (
    // Colonna degli strumenti admin (§8.1): sopra i 768px occupa tutta la
    // finestra con 24px di margine per lato — la tabella ha bisogno di
    // larghezza — e SOTTO i 768px coincide con la colonna standard, quindi
    // il valore non si commuta a mano: lo fa già HomeShell.css, con lo
    // stesso breakpoint. Un secondo interruttore qui sarebbe un secondo
    // metro per la stessa soglia.
    <HomeShell backTo={() => navigate(-1)} showSwitch={false} width="admin">
      <h1 className="qpsc-page-title">Attendance</h1>

      <div className="qpsc-atts-context">
        <span className="qpsc-atts-context__team">{squadra}</span>
        <span className="qpsc-atts-context__average">
          Team average: {mediaSquadra}%
        </span>
      </div>

      <div className="qpsc-atts-actions">
        <button type="button" className="qpsc-tool-btn" onClick={esporta}>
          <FaFileCsv />
          Export CSV
        </button>
      </div>

      {errore && <p className="qpsc-atts-error">{errore}</p>}

      {/* SU TELEFONO: il bivio fra le due letture degli STESSI dati. È il
          gruppo-scelta §6 del telaio, non una coppia di pulsanti nuova. */}
      {compatto && !caricamento && (
        <div className="qpsc-choice-group">
          <span className="qpsc-choice-group__label">Show</span>
          <div className="qpsc-choice-group__options">
            <button
              type="button"
              className={
                "qpsc-btn-dark" +
                (vista === "player" ? " qpsc-choice-option--selected" : "")
              }
              aria-pressed={vista === "player"}
              onClick={() => setVista("player")}
            >
              By player
            </button>
            <button
              type="button"
              className={
                "qpsc-btn-dark" +
                (vista === "event" ? " qpsc-choice-option--selected" : "")
              }
              aria-pressed={vista === "event"}
              onClick={() => setVista("event")}
            >
              By event
            </button>
          </div>
        </div>
      )}

      {caricamento ? (
        <p className="qpsc-atts-empty">Loading attendance…</p>
      ) : players.length === 0 ? (
        <p className="qpsc-atts-empty">No players in this team.</p>
      ) : compatto ? (
        vista === "player" ? (
          <ElencoPerGiocatore
            players={players}
            events={events}
            annoOperativo={annoOperativo}
            aperto={apertoPlayer}
            onApri={(id) => setApertoPlayer((att) => (uguali(att, id) ? "" : String(id)))}
            statsDi={statsDi}
            statoDi={statoDi}
            onCella={apriCella}
            nomeDi={nomeDi}
          />
        ) : (
          <ElencoPerEvento
            players={players}
            events={events}
            annoOperativo={annoOperativo}
            aperto={apertoEvento}
            onApri={(id) => setApertoEvento((att) => (uguali(att, id) ? "" : String(id)))}
            statoDi={statoDi}
            onCella={apriCella}
            nomeDi={nomeDi}
          />
        )
      ) : (
        <GrigliaPresenze
          players={players}
          events={events}
          annoOperativo={annoOperativo}
          statsDi={statsDi}
          statoDi={statoDi}
          onCella={apriCella}
          nomeDi={nomeDi}
          onProfilo={(id) => navigate(`/profile/${id}`)}
        />
      )}

      {cella && (
        <ModaleStato
          cella={cella}
          valore={nuovoStato}
          onValore={setNuovoStato}
          onAnnulla={() => setCella(null)}
          onSalva={salvaCella}
          salvataggio={salvataggio}
        />
      )}
    </HomeShell>
  );
}

// ===========================================================================
// IL SEGNO DI UNO STATO — icona Font Awesome, colore dal token
// ===========================================================================
// I colori vengono dal §1.3: verde per la presenza, rosso LEGGIBILE per
// assenza e sospensione (a questo gradino il Rosso QPSC non raggiunge AA),
// ambra per attesa e infortunio, testo secondario per «nessuna risposta».
// A distinguere le coppie che condividono il colore è l'ICONA: la palette
// del doc 07 è chiusa (§1.4) e non ha un quinto colore da assegnare.
function SegnoStato({ stato }) {
  const voce = STATO_DI[stato];

  if (!voce) {
    return <span className="qpsc-atts-mark qpsc-atts-mark--none">&mdash;</span>;
  }

  const { Icon, etichetta } = voce;

  return (
    <span className={`qpsc-atts-mark qpsc-atts-mark--${stato}`} title={etichetta}>
      <Icon />
    </span>
  );
}

// ===========================================================================
// LA GRIGLIA — resa ≥768px (doc 07 §8.1: sopra la soglia, tabella)
// ===========================================================================
function GrigliaPresenze({
  players,
  events,
  annoOperativo,
  statsDi,
  statoDi,
  onCella,
  nomeDi,
  onProfilo
}) {
  return (
    <div className="qpsc-atts-table-wrap">
      <table className="qpsc-atts-table">
        <thead>
          <tr>
            <th className="qpsc-atts-th qpsc-atts-col-num">#</th>
            <th className="qpsc-atts-th qpsc-atts-col-name">Player</th>
            <th className="qpsc-atts-th qpsc-atts-col-cat">Gender</th>
            <th className="qpsc-atts-th qpsc-atts-col-count" title="Present">
              <FaCheck />
            </th>
            <th className="qpsc-atts-th qpsc-atts-col-count" title="Absent">
              <FaTimes />
            </th>
            <th className="qpsc-atts-th qpsc-atts-col-count" title="Injured">
              <FaNotesMedical />
            </th>
            <th className="qpsc-atts-th qpsc-atts-col-count">%</th>
            {events.map((ev) => (
              <th
                key={ev.id}
                className="qpsc-atts-th qpsc-atts-col-ev"
                title={`${ev.type || ""} ${ev.opponent ? `vs ${ev.opponent}` : ""} ${
                  ev.date
                } ${ev.time || ""}`.trim()}
              >
                <span className="qpsc-atts-evhead">{etichettaBreve(ev)}</span>
                <span
                  className={`qpsc-pill qpsc-atts-type--${
                    eAllenamento(ev) ? "training" : "game"
                  }`}
                >
                  {eAllenamento(ev) ? "TRN" : "GM"}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p, indice) => {
            const s = statsDi(p.id);

            return (
              <tr key={p.id}>
                <td className="qpsc-atts-td qpsc-atts-col-num">{indice + 1}</td>
                <td className="qpsc-atts-td qpsc-atts-col-name">
                  <button
                    type="button"
                    className="qpsc-atts-name"
                    onClick={() => onProfilo(p.id)}
                  >
                    {nomeDi(p)}
                  </button>
                </td>
                <td className="qpsc-atts-td qpsc-atts-col-cat">
                  <PastigliaCategoriaSeNota
                    category={categoriaAnagrafica(p, annoOperativo)}
                  />
                </td>
                <td className="qpsc-atts-td qpsc-atts-col-count qpsc-atts-count--yes">
                  {s.present}
                </td>
                <td className="qpsc-atts-td qpsc-atts-col-count qpsc-atts-count--no">
                  {s.absent}
                </td>
                <td className="qpsc-atts-td qpsc-atts-col-count qpsc-atts-count--injured">
                  {s.injured}
                </td>
                <td className="qpsc-atts-td qpsc-atts-col-count">{s.percent}%</td>

                {events.map((ev) => (
                  <td key={ev.id} className="qpsc-atts-td qpsc-atts-col-ev">
                    <button
                      type="button"
                      className="qpsc-atts-cell"
                      onClick={() => onCella(p, ev)}
                      aria-label={`${nomeDi(p)} — ${etichettaBreve(ev)}`}
                    >
                      <SegnoStato stato={statoDi(p.id, ev.id)} />
                    </button>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// LE CARD PER GIOCATORE — resa <768px (doc 07 §8.1: sotto la soglia, card)
// ===========================================================================
// Testata: nome (L, 700, oro chiaro) + pastiglia categoria (§2). Sotto: i
// contatori e la percentuale. Toccando la testata la card si apre e mostra
// gli eventi con lo stato di QUEL giocatore; toccando una riga si corregge.
function ElencoPerGiocatore({
  players,
  events,
  annoOperativo,
  aperto,
  onApri,
  statsDi,
  statoDi,
  onCella,
  nomeDi
}) {
  return (
    <div className="qpsc-atts-cards">
      {players.map((p) => {
        const s = statsDi(p.id);
        const eAperto = uguali(aperto, p.id);

        return (
          <article key={p.id} className="qpsc-atts-card">
            <button
              type="button"
              className="qpsc-atts-card__head"
              onClick={() => onApri(p.id)}
              aria-expanded={eAperto}
            >
              <span className="qpsc-atts-card__name">{nomeDi(p)}</span>
              <PastigliaCategoriaSeNota
                category={categoriaAnagrafica(p, annoOperativo)}
              />
            </button>

            <div className="qpsc-atts-counters">
              <span className="qpsc-atts-counter qpsc-atts-count--yes">
                <FaCheck /> {s.present}
              </span>
              <span className="qpsc-atts-counter qpsc-atts-count--no">
                <FaTimes /> {s.absent}
              </span>
              <span className="qpsc-atts-counter qpsc-atts-count--injured">
                <FaNotesMedical /> {s.injured}
              </span>
              <span className="qpsc-atts-counter">{s.percent}%</span>
            </div>

            {eAperto && (
              <ul className="qpsc-atts-detail">
                {events.length === 0 ? (
                  <li className="qpsc-atts-detail__empty">No events yet.</li>
                ) : (
                  events.map((ev) => (
                    <li key={ev.id}>
                      <button
                        type="button"
                        className="qpsc-atts-detail__row"
                        onClick={() => onCella(p, ev)}
                      >
                        <span className="qpsc-atts-detail__label">
                          {etichettaBreve(ev)}
                        </span>
                        <span
                          className={`qpsc-pill qpsc-atts-type--${
                            eAllenamento(ev) ? "training" : "game"
                          }`}
                        >
                          {ev.type || (eAllenamento(ev) ? "Training" : "Game")}
                        </span>
                        <SegnoStato stato={statoDi(p.id, ev.id)} />
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ===========================================================================
// LE CARD PER EVENTO — «apri l'allenamento di stasera → spunta chi c'è»
// ===========================================================================
// È un PIVOT LATO CLIENT degli stessi `players` e `attendance` già scaricati:
// nessuna chiamata nuova. Cambia solo quale delle due dimensioni sta in
// testata e quale nell'elenco.
function ElencoPerEvento({
  players,
  events,
  annoOperativo,
  aperto,
  onApri,
  statoDi,
  onCella,
  nomeDi
}) {
  if (events.length === 0) {
    return <p className="qpsc-atts-empty">No events yet.</p>;
  }

  return (
    <div className="qpsc-atts-cards">
      {events.map((ev) => {
        const eAperto = uguali(aperto, ev.id);

        return (
          <article key={ev.id} className="qpsc-atts-card">
            <button
              type="button"
              className="qpsc-atts-card__head"
              onClick={() => onApri(ev.id)}
              aria-expanded={eAperto}
            >
              <span className="qpsc-atts-card__name">{etichettaBreve(ev)}</span>
              <span
                className={`qpsc-pill qpsc-atts-type--${
                  eAllenamento(ev) ? "training" : "game"
                }`}
              >
                {ev.type || (eAllenamento(ev) ? "Training" : "Game")}
              </span>
            </button>

            {(ev.time || ev.location) && (
              <span className="qpsc-atts-card__meta">
                {[ev.time, ev.location].filter(Boolean).join(" · ")}
              </span>
            )}

            {eAperto && (
              <ul className="qpsc-atts-detail">
                {players.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="qpsc-atts-detail__row"
                      onClick={() => onCella(p, ev)}
                    >
                      <span className="qpsc-atts-detail__label">{nomeDi(p)}</span>
                      <PastigliaCategoriaSeNota
                        category={categoriaAnagrafica(p, annoOperativo)}
                      />
                      <SegnoStato stato={statoDi(p.id, ev.id)} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ===========================================================================
// LA MODALE DEI CINQUE STATI (doc 07 §8 «Modali»)
// ===========================================================================
// Guscio, titolo e comandi sono le classi condivise di AdminTools.css
// (.qpsc-modal-overlay, .qpsc-modal, …): la finestra fatta in casa della resa
// storica (`.att-modal` / `.att-box`, fondo e bordi letterali) non esiste più.
function ModaleStato({ cella, valore, onValore, onAnnulla, onSalva, salvataggio }) {
  return (
    <div className="qpsc-modal-overlay" onClick={onAnnulla}>
      <div
        className="qpsc-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="qpsc-modal__title">Edit attendance</h2>

        <div className="qpsc-modal__body">
          <p className="qpsc-modal__note">
            {cella.nome} &mdash; {cella.evento}
          </p>

          <div className="qpsc-choice-group">
            <span className="qpsc-choice-group__label">Status</span>
            <div className="qpsc-choice-group__options">
              {STATI.map(({ valore: v, etichetta, Icon }) => (
                <button
                  key={v}
                  type="button"
                  className={
                    "qpsc-btn-dark" +
                    (valore === v ? " qpsc-choice-option--selected" : "")
                  }
                  aria-pressed={valore === v}
                  onClick={() => onValore(v)}
                >
                  <span className="qpsc-atts-option__icon">
                    <Icon />
                  </span>
                  {etichetta}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onAnnulla}>
            Cancel
          </button>
          <button
            type="button"
            className="qpsc-tool-btn"
            onClick={onSalva}
            disabled={salvataggio}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
